import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card, Notice, useToast } from '@kollez/design-system';
import { apiPost } from '@/lib/api';

/**
 * PR 7 — TPO Graduation Batch page. `/tpo/graduation`. Picks a batch year and a default mode
 * (alumni | end), previews affected memberships via a dryRun call, then applies the transition.
 * Individual overrides are Wave-2 (skipped in this MVP page).
 */

interface BatchResult {
  matched: number;
  transitionedAlumni: number;
  ended: number;
  skipped: number;
  errors: Array<{ membershipId: string; reason: string }>;
  dryRun: boolean;
}

export function TpoGraduationBatchPage() {
  const toast = useToast();
  const currentYear = new Date().getFullYear();
  const [batchYear, setBatchYear] = useState<number>(currentYear - 4);
  const [defaultMode, setDefaultMode] = useState<'alumni' | 'end'>('alumni');
  const [preview, setPreview] = useState<BatchResult | null>(null);

  const yearOptions = useMemo(
    () => Array.from({ length: 10 }, (_, i) => currentYear - i),
    [currentYear],
  );

  const dryRun = useMutation({
    mutationFn: (): Promise<{ batch: BatchResult }> =>
      apiPost(`/tpo/graduation-batches/${batchYear}/apply?dryRun=true`, {
        defaultMode,
      }),
    onSuccess: (data) => setPreview(data.batch),
    onError: (err) => {
      toast.show({
        title: 'Preview failed',
        message: err instanceof Error ? err.message : 'Please try again.',
        tone: 'danger',
      });
    },
  });

  const apply = useMutation({
    mutationFn: (): Promise<{ batch: BatchResult }> =>
      apiPost(`/tpo/graduation-batches/${batchYear}/apply`, {
        defaultMode,
      }),
    onSuccess: (data) => {
      setPreview(data.batch);
      toast.show({
        title: 'Graduation batch applied',
        message: `${data.batch.transitionedAlumni} alumni, ${data.batch.ended} ended, ${data.batch.skipped} skipped.`,
      });
    },
    onError: (err) => {
      toast.show({
        title: 'Batch apply failed',
        message: err instanceof Error ? err.message : 'Please try again.',
        tone: 'danger',
      });
    },
  });

  return (
    <div className="grid gap-4">
      <header className="grid gap-1">
        <h1 className="h-page">Graduate a batch</h1>
        <p className="text-sm" style={{ color: 'rgb(var(--ink-tertiary))' }}>
          Move every verified student in a batch to alumni (D1 — soft) or end (D2 — hard).
          Preview counts first, then apply. Kollez Public memberships are always preserved.
        </p>
      </header>

      <Card>
        <div className="grid gap-3">
          <label className="grid gap-1.5">
            <span className="label">Batch (joining year)</span>
            <select
              className="input"
              value={batchYear}
              onChange={(e) => setBatchYear(Number(e.target.value))}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>

          <label className="grid gap-1.5">
            <span className="label">Default mode</span>
            <div className="grid" style={{ gap: 8 }}>
              <label className="flex items-center" style={{ gap: 8 }}>
                <input
                  type="radio"
                  name="mode"
                  checked={defaultMode === 'alumni'}
                  onChange={() => setDefaultMode('alumni')}
                />
                <span>
                  <b>Alumni</b> — membership stays in the switcher, scope narrows (no current
                  campus drives visible). Recommended default.
                </span>
              </label>
              <label className="flex items-center" style={{ gap: 8 }}>
                <input
                  type="radio"
                  name="mode"
                  checked={defaultMode === 'end'}
                  onChange={() => setDefaultMode('end')}
                />
                <span>
                  <b>End</b> — membership drops from the switcher. Placement history stays
                  visible in Kollez Public workspace.
                </span>
              </label>
            </div>
          </label>

          <div className="flex" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => dryRun.mutate()}
              disabled={dryRun.isPending || apply.isPending}
            >
              Preview counts
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => apply.mutate()}
              disabled={apply.isPending || dryRun.isPending}
            >
              Apply to {batchYear}
            </button>
          </div>
        </div>
      </Card>

      {preview ? (
        <Notice tone={preview.dryRun ? 'info' : 'success'}>
          <div className="grid gap-1">
            <b>{preview.dryRun ? 'Preview' : 'Applied'} — batch {batchYear}, mode {defaultMode}</b>
            <div className="text-sm">
              Matched <b>{preview.matched}</b> · Alumni <b>{preview.transitionedAlumni}</b> ·
              Ended <b>{preview.ended}</b> · Skipped <b>{preview.skipped}</b>
            </div>
            {preview.errors.length > 0 ? (
              <details className="text-sm">
                <summary>{preview.errors.length} errors</summary>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {preview.errors.slice(0, 20).map((e) => (
                    <li key={e.membershipId}>
                      <code>{e.membershipId.slice(-8)}</code>: {e.reason}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        </Notice>
      ) : null}
    </div>
  );
}
